/**
 * What the app paints while a lazy route chunk is still in flight.
 *
 * AIDEV-NOTE [MEETBALL]: this is the GLOBAL Suspense fallback in RootLayout, so
 * it is the load state of nearly every screen in the app, not of one flow. It
 * used to be a blue spinner centred on a bare white page, which was wrong twice
 * over: white is not a colour this product paints anywhere (the door, the
 * transit shell and logto-ui all ground on `#f0efeb`), so every cold chunk cost
 * a white flash between two cream documents; and a spinner on a chunk that
 * usually arrives in tens of milliseconds is a stutter, not information.
 *
 * So: the cream ground and the mark go up together, immediately, on mount --
 * no reveal delay. This used to hold the mark back behind a timer so a fast
 * chunk wouldn't flash it in and out. That reasoning no longer holds: the mark
 * occupies the identical rectangle here as it does in AuthTransitShell and
 * logto-ui's own transit surfaces (cross-document geometry contract, see
 * AuthTransitShell), so painting it instantly reads as the same mark
 * persisting through a chunk load, not as a flash. It is the mark alone -- no
 * copy. This surface has no idea which screen is coming, so any sentence it
 * could write would be either a lie ("Signing you in") or noise ("Loading").
 * AuthTransitShell is the one that knows, and it is the one that speaks.
 *
 * The mark is INLINE SVG, and on this surface that matters twice over: it is the load state
 * of nearly every screen in the app, so an <img> here would start a request on any cold
 * chunk, and on the two transit routes it is what stands between the static twin in
 * index.html and AuthTransitShell. An <img> reintroduces the flash on all of them.
 *
 * There is no animation here, so there is nothing for prefers-reduced-motion to
 * reduce, and none may be added: a fade would fire on every route change and
 * turn a quiet ground into an event.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: "the identical rectangle" is a claim about
 * LAYOUT, and this surface used to break it while passing every test in the
 * suite -- because the pinning test compares path data and fills, which is an
 * ASSET comparison. `AuthTransitShell` lays out a column of mark (52px) + 20px
 * gap + a line box reserved at 24px, centred, so its mark's top sits at
 * `(H - 96) / 2`. This centred the mark ALONE -- the status line is `sr-only`,
 * which is absolutely positioned and contributes no height -- so its mark's top
 * sat at `(H - 52) / 2`, about 22px higher on an 800px viewport. The two render
 * back to back on most sign-ins: `/callback` paints the shell, the destination
 * route is lazy, React suspends, and the global fallback in RootLayout paints
 * this -- same mark, different place -- before the destination lands. A mark
 * that jumps on the last hop undercuts the continuity the inlined static shell
 * was added to create. So the line's BOX is reserved here too, with the copy
 * still sr-only inside it: same column, same gap, same 24px line box, and
 * nothing visible that this surface has no business saying.
 */
import React from 'react';

import { AuthTransitMark } from './AuthTransitMark';

export const RouteFallback: React.FC = () => {
  return (
    <div
      data-testid="route-fallback"
      className="fixed inset-0 z-1 flex flex-col items-center justify-center gap-5 bg-mb-bg px-6"
    >
      {/* Inline SVG, the same component the transit shell renders, so a chunk load that
          lands between two transit documents swaps identical pixels rather than starting a
          request. Hidden from AT: the sr-only status line below is the accessible name of
          the wait, and a named mark would announce the brand instead. */}
      <AuthTransitMark data-testid="route-fallback-mark" />
      {/* `min-h-6` on the OUTER element and `sr-only` on the text, not `sr-only` on both:
          the 24px line box is what keeps this column the same height as AuthTransitShell's,
          and an sr-only element is `position: absolute` so it reserves nothing. Reading it
          the other way round is what put the mark 22px high. */}
      <p data-testid="route-fallback-line" className="min-h-6" role="status" aria-live="polite">
        <span className="sr-only">Loading</span>
      </p>
    </div>
  );
};
